import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Icon } from './Icon';

interface QrCardProps {
  token: string;
  code: string;
  compact?: boolean;
}

export function QrCard({ token, code, compact = false }: QrCardProps): JSX.Element {
  const [image, setImage] = useState<string>();

  useEffect(() => {
    let current = true;
    const canvas = document.createElement('canvas');
    void QRCode.toCanvas(canvas, token, {
      margin: 4,
      width: 720,
      color: { dark: '#03045e', light: '#f6fbff' },
      errorCorrectionLevel: 'H',
    }).then(() => new Promise<void>((resolve) => {
      const logo = new Image();
      logo.src = '/assets/logo-forteens.png';
      logo.onload = () => {
        const context = canvas.getContext('2d');
        if (context) {
          // Crop transparent pixels from the supplied wordmark first. It lets
          // the white outlined text fill a fixed square badge without making
          // the QR overlay larger than high error correction can tolerate.
          const logoCanvas = document.createElement('canvas');
          logoCanvas.width = logo.naturalWidth || logo.width;
          logoCanvas.height = logo.naturalHeight || logo.height;
          const logoContext = logoCanvas.getContext('2d', { willReadFrequently: true });
          logoContext?.drawImage(logo, 0, 0);
          const pixels = logoContext?.getImageData(0, 0, logoCanvas.width, logoCanvas.height).data;
          let left = logoCanvas.width; let top = logoCanvas.height; let right = -1; let bottom = -1;
          if (pixels) for (let index = 3; index < pixels.length; index += 4) {
            if (pixels[index]! < 12) continue;
            const pixel = (index - 3) / 4; const px = pixel % logoCanvas.width; const py = Math.floor(pixel / logoCanvas.width);
            left = Math.min(left, px); top = Math.min(top, py); right = Math.max(right, px); bottom = Math.max(bottom, py);
          }
          const sourceX = right >= left ? left : 0; const sourceY = bottom >= top ? top : 0;
          const sourceWidth = right >= left ? right - left + 1 : logoCanvas.width;
          const sourceHeight = bottom >= top ? bottom - top + 1 : logoCanvas.height;
          const badgeSize = Math.round(canvas.width * .19);
          const x = Math.round((canvas.width - badgeSize) / 2);
          const y = Math.round((canvas.height - badgeSize) / 2);
          context.fillStyle = '#f6fbff';
          context.beginPath(); context.roundRect(x, y, badgeSize, badgeSize, 16); context.fill();
          context.fillStyle = '#03045e';
          context.beginPath(); context.roundRect(x + 8, y + 8, badgeSize - 16, badgeSize - 16, 11); context.fill();
          const ratio = Math.min((badgeSize - 24) / sourceWidth, (badgeSize - 24) / sourceHeight);
          const width = sourceWidth * ratio; const height = sourceHeight * ratio;
          context.drawImage(logoCanvas, sourceX, sourceY, sourceWidth, sourceHeight, x + ((badgeSize - width) / 2), y + ((badgeSize - height) / 2), width, height);
        }
        resolve();
      };
      logo.onerror = () => resolve();
    })).then(() => {
      if (current) setImage(canvas.toDataURL('image/png'));
    }).catch(() => {
      if (current) setImage(undefined);
    });
    return () => { current = false; };
  }, [token]);

  return (
    <section className={`qr-card ${compact ? 'qr-card--compact' : ''}`}>
      <div className="qr-card__copy">
        <span className="eyebrow"><Icon name="qr" /> SEU ACESSO</span>
        <h3>QR Code liberado</h3>
        <p>Apresente este código na entrada. Não envie capturas para desconhecidos.</p>
      </div>
      <div className="qr-card__image" aria-label={`QR Code da inscrição ${code}`}>
        {image ? <img src={image} alt={`QR Code da inscrição ${code}`} /> : <span className="qr-card__loading">Gerando código…</span>}
      </div>
      <code>{code}</code>
    </section>
  );
}
